import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, FileText, UserCheck, Database, Bell, Trash2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="container-app py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Shield className="w-16 h-16 text-blue-400 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-white">
              Privacy Policy
            </h1>
            <p className="mt-4 text-slate-300 text-lg">
              Compliant with Digital Personal Data Protection Act, 2023 (India)
            </p>
          </motion.div>
        </div>
      </div>
      
      <div className="container-app py-12">
        <div className="max-w-4xl mx-auto">
          {/* DPDP Compliance Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-start gap-4">
              <FileText className="w-6 h-6 text-blue-600 shrink-0 mt-1" />
              <div>
                <h2 className="font-bold text-blue-900 mb-2">DPDP Act 2023 Compliance</h2>
                <p className="text-blue-800 text-sm">
                  This Privacy Policy is designed to comply with the Digital Personal Data Protection Act, 2023 
                  (DPDP Act) of India. We are committed to protecting your personal data and ensuring transparency 
                  in how we collect, process, and store your information.
                </p>
              </div>
            </div>
          </motion.div>
          
          <p className="text-slate-600 mb-8">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          
          {/* Data Fiduciary Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-8 border border-slate-200 mb-6"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <Database className="w-6 h-6 text-blue-600" />
              Data Fiduciary Details
            </h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-slate-500">Data Fiduciary</p>
                <p className="font-semibold text-slate-900">LegalHub</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-slate-500">Registered Address</p>
                <p className="font-semibold text-slate-900">Mumbai, Maharashtra, India</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-slate-500">Data Protection Officer</p>
                <p className="font-semibold text-slate-900">LegalHub Team</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-slate-500">Contact Email</p>
                <p className="font-semibold text-slate-900">info@legalhubs.info</p>
                <p className="font-semibold text-slate-900">deepakrao1983@gmail.com</p>
              </div>
            </div>
          </motion.div>

          {/* Information We Collect */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-8 border border-slate-200 mb-6"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <Eye className="w-6 h-6 text-blue-600" />
              Personal Data We Collect
            </h2>
            <p className="text-slate-600 mb-4">
              Under the DPDP Act, we collect the following categories of personal data with your explicit consent:
            </p>
            
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-slate-900 mb-2">For Clients (Data Principals)</h3>
                <ul className="list-disc list-inside text-slate-600 text-sm space-y-1">
                  <li>Name, email address, and mobile number</li>
                  <li>Location/city for lawyer matching</li>
                  <li>Case descriptions and legal requirements</li>
                  <li>Payment transaction details</li>
                  <li>Communication logs with lawyers</li>
                </ul>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-slate-900 mb-2">For Lawyers</h3>
                <ul className="list-disc list-inside text-slate-600 text-sm space-y-1">
                  <li>Name, email, mobile number, and office address</li>
                  <li>Bar Council enrollment number and certificate</li>
                  <li>Educational qualifications and experience</li>
                  <li>Practice areas and consultation fees</li>
                  <li>Bank account details for payouts</li>
                  <li>Government ID (for verification)</li>
                </ul>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-slate-900 mb-2">Automatically Collected Data</h3>
                <ul className="list-disc list-inside text-slate-600 text-sm space-y-1">
                  <li>IP address and device information</li>
                  <li>Browser type and operating system</li>
                  <li>Pages visited and time spent on platform</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Purpose of Processing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-8 border border-slate-200 mb-6"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <UserCheck className="w-6 h-6 text-blue-600" />
              Purpose of Data Processing
            </h2>
            <p className="text-slate-600 mb-4">
              We process your personal data only for lawful purposes as specified under DPDP Act Section 4:
            </p>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">1</span>
                <span><strong>Service Delivery:</strong> To connect clients with lawyers, process bookings, and facilitate consultations</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">2</span>
                <span><strong>Verification:</strong> To verify lawyer credentials with Bar Council of India</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">3</span>
                <span><strong>Payments:</strong> To process consultation fees and lawyer payouts</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">4</span>
                <span><strong>Communication:</strong> To send booking confirmations, reminders, and support messages</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">5</span>
                <span><strong>Legal Compliance:</strong> To comply with applicable laws and respond to legal requests</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">6</span>
                <span><strong>Platform Improvement:</strong> To analyze usage and improve our services</span>
              </li>
            </ul>
          </motion.div>

          {/* Your Rights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-amber-50 border border-amber-200 rounded-2xl p-8 mb-6"
          >
            <h2 className="text-xl font-bold text-amber-900 mb-4 flex items-center gap-3">
              <Shield className="w-6 h-6 text-amber-600" />
              Your Rights Under DPDP Act 2023
            </h2>
            <p className="text-amber-800 mb-4">
              As a Data Principal, you have the following rights:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-1">Right to Access</h3>
                <p className="text-sm text-slate-600">Request information about your personal data being processed</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-1">Right to Correction</h3>
                <p className="text-sm text-slate-600">Request correction of inaccurate or incomplete data</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-1">Right to Erasure</h3>
                <p className="text-sm text-slate-600">Request deletion of your personal data (subject to legal requirements)</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-1">Right to Grievance Redressal</h3>
                <p className="text-sm text-slate-600">File complaints with our Data Protection Officer</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-1">Right to Nominate</h3>
                <p className="text-sm text-slate-600">Nominate another person to exercise rights on your behalf</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-1">Right to Withdraw Consent</h3>
                <p className="text-sm text-slate-600">Withdraw consent at any time with ease</p>
              </div>
            </div>
            <p className="text-amber-800 text-sm mt-4">
              To exercise any of these rights, contact us at <strong>info@legalhubs.info</strong>. 
              We will respond within 30 days as mandated by law.
            </p>
          </motion.div>

          {/* Data Security */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl p-8 border border-slate-200 mb-6"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <Lock className="w-6 h-6 text-blue-600" />
              Data Security Measures
            </h2>
            <p className="text-slate-600 mb-4">
              We implement reasonable security safeguards as required under DPDP Act Section 8:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <span className="text-green-600">✓</span>
                <span className="text-sm text-slate-600">256-bit SSL/TLS encryption for all data transmission</span>
              </div>
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <span className="text-green-600">✓</span>
                <span className="text-sm text-slate-600">Encrypted database storage with access controls</span>
              </div>
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <span className="text-green-600">✓</span>
                <span className="text-sm text-slate-600">Secure password hashing (bcrypt)</span>
              </div>
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <span className="text-green-600">✓</span>
                <span className="text-sm text-slate-600">Regular security audits and vulnerability assessments</span>
              </div>
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <span className="text-green-600">✓</span>
                <span className="text-sm text-slate-600">Role-based access controls for employees</span>
              </div>
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <span className="text-green-600">✓</span>
                <span className="text-sm text-slate-600">Data breach notification within 72 hours</span>
              </div>
            </div>
          </motion.div>

          {/* Data Retention */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-2xl p-8 border border-slate-200 mb-6"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <Trash2 className="w-6 h-6 text-blue-600" />
              Data Retention & Deletion
            </h2>
            <div className="space-y-4 text-slate-600">
              <p>We retain your personal data only for as long as necessary:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Active Accounts:</strong> Data retained while account is active</li>
                <li><strong>Inactive Accounts:</strong> Deleted after 3 years of inactivity</li>
                <li><strong>Transaction Records:</strong> Retained for 7 years (as per tax laws)</li>
                <li><strong>Communication Logs:</strong> Retained for 2 years</li>
                <li><strong>Analytics Data:</strong> Anonymized after 1 year</li>
              </ul>
              <p className="mt-4">
                You can request immediate deletion of your account and personal data by contacting 
                our DPO. Note: Some data may be retained for legal compliance.
              </p>
            </div>
          </motion.div>

          {/* Third Party Sharing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white rounded-2xl p-8 border border-slate-200 mb-6"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <Bell className="w-6 h-6 text-blue-600" />
              Data Sharing with Third Parties
            </h2>
            <p className="text-slate-600 mb-4">
              We may share your data with the following categories of recipients:
            </p>
            <div className="space-y-3">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-slate-900">Lawyers</h3>
                <p className="text-sm text-slate-600">When you book a consultation, your case details are shared with the selected lawyer</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-slate-900">Payment Processors</h3>
                <p className="text-sm text-slate-600">Razorpay processes payments; we do not store card details</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-slate-900">Cloud Service Providers</h3>
                <p className="text-sm text-slate-600">Data stored on secure servers in India</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-slate-900">Legal Authorities</h3>
                <p className="text-sm text-slate-600">When required by law, court order, or government request</p>
              </div>
            </div>
            <p className="text-slate-600 text-sm mt-4">
              <strong>Note:</strong> We do not sell your personal data to third parties for marketing purposes.
            </p>
          </motion.div>

          {/* Contact DPO */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-blue-900 text-white rounded-2xl p-8 text-center"
          >
            <h2 className="text-xl font-bold mb-4">Contact Us for Privacy Queries</h2>
            <p className="text-blue-100 mb-4">
              For any privacy-related queries, concerns, or to exercise your rights:
            </p>
            <div className="bg-blue-800 rounded-lg p-4 inline-block">
              <p><strong>Email:</strong> info@legalhubs.info</p>
              <p><strong>Phone:</strong> +91 9076433479</p>
            </div>
            <p className="text-blue-200 text-sm mt-4">
              We will acknowledge your request within 48 hours and resolve within 30 days.
            </p>
          </motion.div>

          <div className="mt-8 text-center">
            <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
